import React, { useMemo } from 'react';
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  IconButton,
  Collapse,
  Typography
} from '@mui/material';
import { NumericFormat } from 'react-number-format';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { getAllowedUnits } from 'utils/forms/cuffa_functions';

export type CuffaMeasurementUnitEnum = any;
export type CuffaProductionTypeEnum = any;
export type CuffaProductTypeEnum = any;

export interface ProdutoSelecionado {
  uuid: string;
  name: string;
  selecionado: boolean;
  expandido: boolean;
  production_area_hectares: number | null;
  volume_produced: number | null;
  volume_produced_unit: CuffaMeasurementUnitEnum;
  volume_commercialized: number | null;
  volume_commercialized_unit: CuffaMeasurementUnitEnum;
  average_commercialized_value: number | null;
  volume_self_consumption: number | null;
  volume_self_consumption_unit: CuffaMeasurementUnitEnum;
  volume_lost: number | null;
  volume_lost_unit: CuffaMeasurementUnitEnum;
  is_processed: boolean;
  production_type: CuffaProductionTypeEnum;
  product_type: CuffaProductTypeEnum;
  availability_months: Record<string, any> & { available_year_round: boolean };
}

export const ProdutoDetalhes: React.NamedExoticComponent<{
  produto: ProdutoSelecionado;
  onChange: (uuid: string, campo: string, valor: any) => void;
  focusedFieldRef?: any;
}> = React.memo(({ produto, onChange }) => {
  const unidadesPermitidas = useMemo(() => (
    getAllowedUnits(
      produto.name,
      produto.production_type,
      produto.is_processed,
      produto.product_type
    )
  ), [produto.name, produto.production_type, produto.is_processed, produto.product_type]);

  const somenteUmaUnidade = unidadesPermitidas.length === 1;

  return (
    <Box sx={{ p: 2, border: '1px solid #eee', borderRadius: 1 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <NumericFormat
            customInput={TextField}
            label="Área de produção (hectares)"
            fullWidth
            value={produto.production_area_hectares ?? ''}
            onValueChange={(values) => onChange(
              produto.uuid,
              'production_area_hectares',
              values.floatValue
            )}
            decimalScale={2}
            allowNegative={false}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <NumericFormat
            customInput={TextField}
            label="Volume produzido"
            fullWidth
            value={produto.volume_produced ?? ''}
            onValueChange={(values) => onChange(
              produto.uuid,
              'volume_produced',
              values.floatValue
            )}
            decimalScale={2}
            allowNegative={false}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            select
            label="Unidade de volume produzido"
            fullWidth
            value={produto.volume_produced_unit}
            disabled={somenteUmaUnidade}
            onChange={(e) => onChange(
              produto.uuid,
              'volume_produced_unit',
              e.target.value
            )}
          >
            {unidadesPermitidas.map((unit: any) => (
              <MenuItem key={unit} value={unit}>
                {unit}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <NumericFormat
            customInput={TextField}
            label="Volume comercializado"
            fullWidth
            value={produto.volume_commercialized ?? produto.volume_commercialized ?? produto.volume_commercialized}
            onValueChange={(values) => onChange(
              produto.uuid,
              'volume_commercialized',
              values.floatValue
            )}
            decimalScale={2}
            allowNegative={false}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            select
            label="Unidade de volume comercializado"
            fullWidth
            value={produto.volume_commercialized_unit}
            disabled={somenteUmaUnidade}
            onChange={(e) => onChange(
              produto.uuid,
              'volume_commercialized_unit',
              e.target.value
            )}
          >
            {unidadesPermitidas.map((unit: any) => (
              <MenuItem key={unit} value={unit}>
                {unit}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <NumericFormat
            customInput={TextField}
            label="Valor médio comercializado (R$)"
            fullWidth
            value={produto.average_commercialized_value ?? ''}
            onValueChange={(values) => onChange(
              produto.uuid,
              'average_commercialized_value',
              values.floatValue
            )}
            decimalScale={2}
            allowNegative={false}
            thousandSeparator="."
            decimalSeparator=","
            prefix="R$ "
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <NumericFormat
            customInput={TextField}
            label="Volume autoconsumido"
            fullWidth
            value={produto.volume_self_consumption ?? ''}
            onValueChange={(values) => onChange(
              produto.uuid,
              'volume_self_consumption',
              values.floatValue
            )}
            decimalScale={2}
            allowNegative={false}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            select
            label="Unidade de volume autoconsumido"
            fullWidth
            value={produto.volume_self_consumption_unit}
            disabled={somenteUmaUnidade}
            onChange={(e) => onChange(
              produto.uuid,
              'volume_self_consumption_unit',
              e.target.value
            )}
          >
            {unidadesPermitidas.map((unit: any) => (
              <MenuItem key={unit} value={unit}>
                {unit}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <NumericFormat
            customInput={TextField}
            label="Volume perdido"
            fullWidth
            value={produto.volume_lost ?? ''}
            onValueChange={(values) => onChange(
              produto.uuid,
              'volume_lost',
              values.floatValue
            )}
            decimalScale={2}
            allowNegative={false}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            select
            label="Unidade de volume perdido"
            fullWidth
            value={produto.volume_lost_unit}
            disabled={somenteUmaUnidade}
            onChange={(e) => onChange(
              produto.uuid,
              'volume_lost_unit',
              e.target.value
            )}
          >
            {unidadesPermitidas.map((unit: any) => (
              <MenuItem key={unit} value={unit}>
                {unit}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <FormControl component="fieldset">
            <FormLabel component="legend">
              Meses de disponibilidade para comercialização:
            </FormLabel>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={produto.availability_months.available_year_round}
                    onChange={(e) => onChange(
                      produto.uuid,
                      'availability_months.available_year_round',
                      e.target.checked
                    )}
                  />
                }
                label="Disponível o ano todo"
              />
              {!produto.availability_months.available_year_round && (
                <>
                  {[
                    'january', 'february', 'march', 'april',
                    'may', 'june', 'july', 'august',
                    'september', 'october', 'november', 'december'
                  ].map((mes) => {
                    const monthLabels: { [key: string]: string } = {
                      january: 'Jan', february: 'Fev', march: 'Mar', april: 'Abr',
                      may: 'Mai', june: 'Jun', july: 'Jul', august: 'Ago',
                      september: 'Set', october: 'Out', november: 'Nov', december: 'Dez'
                    };
                    return (
                      <FormControlLabel
                        key={mes}
                        control={
                          <Checkbox
                            checked={produto.availability_months[mes as keyof typeof produto.availability_months]}
                            onChange={(e) => onChange(
                              produto.uuid,
                              `availability_months.${mes}`,
                              e.target.checked
                            )}
                          />
                        }
                        label={monthLabels[mes] || mes}
                      />
                    );
                  })}
                </>
              )}
            </FormGroup>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
});

ProdutoDetalhes.displayName = 'ProdutoDetalhesShared';

export const ProdutoItem: React.NamedExoticComponent<{
  produto: ProdutoSelecionado;
  onSelecionar: (uuid: string) => void;
  onExpandir: (uuid: string) => void;
  onChange: (uuid: string, campo: string, valor: any) => void;
}> = React.memo(({ produto, onSelecionar, onExpandir, onChange }) => {
  return (
    <Box mb={1}>
      <Box
        display="flex"
        alignItems="center"
        sx={{
          p: 1,
          backgroundColor: produto.selecionado ? '#f5f5f5' : 'transparent',
          cursor: 'pointer',
          border: '1px solid #ddd',
          borderRadius: 1
        }}
      >
        <Checkbox
          checked={produto.selecionado}
          onChange={() => onSelecionar(produto.uuid)}
          size="small"
        />
        <Typography
          flexGrow={1}
          onClick={() => onSelecionar(produto.uuid)}
          variant="body2"
        >
          {produto.name}
        </Typography>
        {produto.selecionado && (
          <IconButton
            onClick={() => onExpandir(produto.uuid)}
            size="small"
          >
            {produto.expandido ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        )}
      </Box>

      <Collapse in={produto.selecionado && produto.expandido} unmountOnExit>
        <ProdutoDetalhes produto={produto} onChange={onChange} />
      </Collapse>
    </Box>
  );
});

ProdutoItem.displayName = 'ProdutoItemShared';

export default ProdutoDetalhes;
